import { create } from 'zustand';
import {
  getCachedScene,
  saveSceneLocally,
  listCachedSceneIds,
  saveAssetLocally,
  queueSync,
  processSyncQueue,
} from '../lib/offline-manager';
import { api } from '../lib/api-client';
import type { SceneData, SyncAction, Asset } from '@spine-workspace/shared';

interface OfflineState {
  isOnline: boolean;
  pendingSyncs: SyncAction[];
  cachedSceneIds: string[];

  setOnline: (online: boolean) => void;
  downloadForOffline: (sceneId: string) => Promise<void>;
  saveOffline: (sceneId: string, sceneData: SceneData, localVersion: number) => Promise<void>;
  syncPending: () => Promise<void>;
  getCachedScene: (sceneId: string) => Promise<SceneData | null>;
  loadCachedIds: () => Promise<void>;
}

export const useOfflineStore = create<OfflineState>((set) => ({
  isOnline: navigator.onLine,
  pendingSyncs: [],
  cachedSceneIds: [],

  setOnline: (isOnline) => set({ isOnline }),

  loadCachedIds: async () => {
    const ids = await listCachedSceneIds();
    set({ cachedSceneIds: ids });
  },

  downloadForOffline: async (sceneId) => {
    const { scene } = await api.get<{ scene: { id: string; sceneData: SceneData; localVersion: number; sceneData_raw?: string } }>(`/api/scenes/${sceneId}`);
    await saveSceneLocally(sceneId, scene.sceneData, scene.localVersion, scene.localVersion);

    // Pre-cache all asset files used in this scene
    const assetIds = [...new Set((scene.sceneData?.layers ?? []).map((l: { assetId: string }) => l.assetId))];
    for (const assetId of assetIds) {
      try {
        const { asset } = await api.get<{ asset: Asset }>(`/api/assets/${assetId}`);
        const [jsonBlob, atlasBlob, ...pngBlobs] = await Promise.all([
          fetch(asset.jsonUrl!).then((r) => r.blob()),
          fetch(asset.atlasUrl!).then((r) => r.blob()),
          ...(asset.pngUrls ?? []).map((url) => fetch(url).then((r) => r.blob())),
        ]);
        await saveAssetLocally(assetId, asset.metadata, jsonBlob, atlasBlob, pngBlobs);
      } catch {
        console.warn(`Could not cache asset ${assetId}`);
      }
    }

    set((s) => ({ cachedSceneIds: [...new Set([...s.cachedSceneIds, sceneId])] }));
  },

  saveOffline: async (sceneId, sceneData, localVersion) => {
    await saveSceneLocally(sceneId, sceneData, localVersion);
    await queueSync('save_scene', { sceneId, sceneData, localVersion });
    set((s) => ({
      pendingSyncs: [...s.pendingSyncs, { id: String(Date.now()), action: 'save_scene', payload: { sceneId }, timestamp: Date.now(), status: 'pending' }],
    }));
  },

  syncPending: async () => {
    const { synced } = await processSyncQueue(async (action) => {
      if (action.action === 'save_scene') {
        const { sceneId, sceneData } = action.payload as { sceneId: string; sceneData: SceneData };
        await api.put(`/api/scenes/${sceneId}`, { sceneData });
      }
    });
    if (synced > 0) {
      set({ pendingSyncs: [] });
    }
  },

  getCachedScene,
}));
